import { useMemo } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import type { DayInfo } from "@/types"
import { DAY_TYPE_META, MONTH_NAMES, WEEKDAYS_SHORT, todayIso, weekdayIndex } from "./dayTypes"

interface YearCalendarGridProps {
  days: DayInfo[]
  /** Klick auf eine Tageszelle — anchor ist das Zellen-Element (fuer das Popover) */
  onDayClick: (day: DayInfo, anchor: HTMLElement) => void
  selectedDate?: string | null
}

/** 12 Monats-Karten mit farbcodierten Tageszellen (Tagtyp-Kaskade, G-31) */
export function YearCalendarGrid({ days, onDayClick, selectedDate }: YearCalendarGridProps) {
  const today = todayIso()

  const months = useMemo(() => {
    const byMonth: DayInfo[][] = Array.from({ length: 12 }, () => [])
    for (const day of days) {
      const month = Number(day.date.slice(5, 7))
      byMonth[month - 1].push(day)
    }
    return byMonth
  }, [days])

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 2xl:grid-cols-3">
      {months.map((monthDays, i) => (
        <Card key={i}>
          <CardHeader className="pb-2 pt-4">
            <CardTitle className="text-sm font-semibold">{MONTH_NAMES[i]}</CardTitle>
          </CardHeader>
          <CardContent className="pb-4">
            <div className="grid grid-cols-7 gap-0.5 text-center">
              {WEEKDAYS_SHORT.map((wd) => (
                <div key={wd} className="py-0.5 text-[10px] font-medium text-muted-foreground">
                  {wd}
                </div>
              ))}
              {monthDays.length > 0 &&
                Array.from({ length: weekdayIndex(monthDays[0].date) }, (_, k) => (
                  <div key={`pad-${k}`} />
                ))}
              {monthDays.map((day) => {
                const meta = DAY_TYPE_META[day.dayType]
                const tooltipParts = [meta.label]
                if (day.holidayName) tooltipParts.push(day.holidayName)
                if (day.note) tooltipParts.push(day.note)
                return (
                  <button
                    key={day.date}
                    type="button"
                    title={tooltipParts.join(" — ")}
                    onClick={(e) => onDayClick(day, e.currentTarget)}
                    className={cn(
                      "flex h-7 items-center justify-center rounded text-xs tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                      meta.cellClass,
                      day.date === today && "font-bold ring-1 ring-primary",
                      day.date === selectedDate && "ring-2 ring-ring"
                    )}
                  >
                    {Number(day.date.slice(8, 10))}
                  </button>
                )
              })}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}
